import { isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { StatusBadge } from "../../components/index.ts";
import { gatewayLevel, gatewayStateLabel, formatTime, type SystemHealth } from "./vocab.ts";

/**
 * State Gateway availability (task 11.15; design 12.2 "State Gateway open/temporarily closed state",
 * 6.4.1, 12.3). The State Gateway admits writes to the authoritative state store; it can be OPEN or
 * TEMPORARILY CLOSED.
 *
 * The honesty rule this panel exists to enforce (design 6.4.1 / 12.3): a TEMPORARILY-CLOSED gateway
 * is a VALID DEGRADED state, surfaced HONESTLY — it is NOT a failure (nothing broke; the gateway is
 * deliberately holding writes while it recovers) and it is NOT green/healthy (it is not fully
 * available). So it renders as an icon+text `warn` with the server's own `reason` and the time it
 * entered the state, never as "all good" and never as "down". An OPEN gateway is the only healthy
 * state.
 *
 * This comes from the REST snapshot (there is no `system` live-stream topic in the contract), so it
 * refreshes on the poll cadence; the `since`/`reason` are shown exactly as reported, with an honest
 * em dash when the server did not supply a reason.
 */

export interface StateGatewayPanelProps {
  readonly gateway: SystemHealth["state_gateway"];
}

export function StateGatewayPanel(props: StateGatewayPanelProps): ReactNode {
  const { gateway } = props;
  const closed = gateway.state === "temporarily_closed";

  const title = closed
    ? "The State Gateway is temporarily closed, a valid degraded state while it recovers, not a failure."
    : "The State Gateway is open and admitting writes.";

  return (
    <section className="sys-region" aria-label="State Gateway availability">
      <h2 className="sys-region__title">State Gateway</h2>
      <p className="sys-region__note sys-faint">
        Whether the gateway is admitting writes to the authoritative state. A temporarily-closed
        gateway is an expected degraded state while it recovers. It is surfaced honestly, not as a
        failure and not as healthy.
      </p>

      <div className="sys-gateway" data-closed={closed || undefined}>
        <StatusBadge level={gatewayLevel(gateway.state)} label={gatewayStateLabel(gateway.state)} title={title} />

        <dl className="sys-gateway__facts">
          <div className="sys-gateway__fact">
            <dt>Since</dt>
            <dd>
              {gateway.since === null ? (
                <span className="sys-muted">n/a</span>
              ) : (
                <time dateTime={isoAttr(gateway.since)} title={isoAttr(gateway.since)}>
                  {formatTime(gateway.since)}
                </time>
              )}
            </dd>
          </div>
          <div className="sys-gateway__fact">
            <dt>Reason</dt>
            <dd>{gateway.reason === null || gateway.reason.length === 0 ? <span className="sys-muted">n/a</span> : gateway.reason}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
